/* eslint-disable no-param-reassign */
import Accordion from '@base/Accordion/Accordion';

import LinearProgressBar from '@base/LinearProgressBar/LinearProgressBar';
import styled from '@emotion/styled';
import ChevronDown from '@icon/ChevronDown';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import { pluralize } from '@utils/textHelper';
import Typography from 'aquilla/core/atoms/Typography';
import { useEffect, useRef } from 'react';
import { isCompletedWithNoVideoUrl, isPostClickable } from '@models/Item';
import { DEFAULT_SUBJECT_VALUE, SESSION_TYPE_MAP } from '../utils';
import RecordedBatchItem from './RecordedBatchItem';

const StyledAccordian = styled(Accordion)`
  max-width: 768px;
  &.MuiAccordion-root {
    padding: ${Spacings.SPACING_4B} 0;
    padding-bottom: 0;
    margin: 0;
    box-shadow: none;
    background-color: transparent;
    margin-bottom: ${Spacings.SPACING_2B};
    border: 0;
    ${mediaQuery.uptoMobile} {
      margin-bottom: 0;
    }
    &.Mui-expanded {
      border: 0;
    }
    pointer-events: ${({ $isDisabled }) => ($isDisabled ? 'none' : 'unset')};
    .MuiIconButton-label {
      opacity: ${({ $isDisabled }) => ($isDisabled ? '0.3' : '1')};
    }
  }
`;

const StyledAccordionSummary = styled(Accordion.Summary)`
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.P1};
  line-height: ${Fonts.PARA_LINE_HEIGHT};
  margin-left: -${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_2B};
  }
  &.MuiAccordionSummary-root {
    color: var(--color-text-primary);
    padding-left: ${Spacings.SPACING_4B};
    &.Mui-expanded {
      padding-left: ${Spacings.SPACING_4B};
      border: 0;
    }
  }
`;

const StyledAccordionDetails = styled(Accordion.Details)`
  font-size: ${Fonts.P1};
  line-height: ${Fonts.PARA_LINE_HEIGHT};
  color: var(--color-text-secondary);
`;

const WeekHeader = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  margin-right: ${Spacings.SPACING_1B};
  justify-content: space-between;
`;

const InlineWrapper = styled.div`
  display: inline-flex;
  gap: ${Spacings.SPACING_2B};
`;

const WeekTitle = styled(Typography)`
  margin-bottom: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.H5};
    margin-bottom: ${Spacings.SPACING_1B};
  }
`;

const WeekSubText = styled(Typography)`
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.ULTRA_SMALL};
  }
`;

const getIsWeekDisabled = (
  sessionType,
  selectedSubject,
  classesCount,
  testsCount
) => {
  if (sessionType !== SESSION_TYPE_MAP.ALL) {
    return sessionType === SESSION_TYPE_MAP.CLASSES
      ? !classesCount
      : !testsCount;
  }

  if (selectedSubject !== DEFAULT_SUBJECT_VALUE) {
    return classesCount === 0 && testsCount === 0;
  }

  return classesCount === 0 && testsCount === 0;
};

const Week = ({
  data,
  isExpanded,
  onWeekSelect,
  sessionType,
  selectedSubject,
  weeksRef,
  hasAccessToBatch,
  batchGoalUID
}) => {
  const {
    weekLabel,
    weekNumber,
    noOfClasses,
    noOfTests,
    progressPercent,
    items,
    subjectData
  } = data;
  const currentWeekRef = useRef();

  useEffect(() => {
    weeksRef.current[weekNumber] = currentWeekRef;
  }, [weeksRef, weekNumber]);

  const handleWeekClick = () => {
    onWeekSelect(weekNumber);
  };

  let filteredList =
    sessionType && sessionType !== SESSION_TYPE_MAP.ALL
      ? items?.filter((item) =>
          sessionType === SESSION_TYPE_MAP.CLASSES
            ? item.type === 'post'
            : item.type !== 'post'
        )
      : items;

  const classesCount =
    selectedSubject === DEFAULT_SUBJECT_VALUE || !selectedSubject
      ? noOfClasses
      : subjectData[selectedSubject.toLowerCase()]?.noOfClasses || 0;

  const testsCount =
    selectedSubject === DEFAULT_SUBJECT_VALUE || !selectedSubject
      ? noOfTests
      : subjectData[selectedSubject]?.noOfTests || 0;

  if (selectedSubject && selectedSubject !== DEFAULT_SUBJECT_VALUE) {
    filteredList = filteredList?.filter(
      (item) =>
        item.topicGroups &&
        item.topicGroups[0] &&
        item.topicGroups[0].uid === selectedSubject
    );
  }

  const isPlusLessonClickable = (lesson) => {
    if (isCompletedWithNoVideoUrl(lesson)) return false;
    return isPostClickable(lesson);
  };

  const onItemClick = (lesson) => {
    const { type, slug, uid } = lesson;
    if (type === 'post' && hasAccessToBatch && isPlusLessonClickable(lesson)) {
      window.open(`/class/${slug}/${uid}`, '_blank');
    } else if (type === 'quiz') {
      window.open(`/quiz/${slug}/${uid}`, '_blank');
    }
  };

  const isWeekDisabled = getIsWeekDisabled(
    sessionType,
    selectedSubject,
    classesCount,
    testsCount
  );

  return (
    <div ref={currentWeekRef}>
      <StyledAccordian
        expanded={isExpanded}
        showExpandedBorder={false}
        $isDisabled={isWeekDisabled}
      >
        <StyledAccordionSummary
          $isExpanded={isExpanded}
          expandIcon={<ChevronDown size={40} />}
          onClick={handleWeekClick}
        >
          <WeekHeader>
            <div>
              <WeekTitle variant="h4">{weekLabel}</WeekTitle>
              <WeekSubText variant="p2" color="var(--color-text-secondary)">
                {sessionType &&
                  (sessionType === SESSION_TYPE_MAP.CLASSES ||
                    sessionType === SESSION_TYPE_MAP.ALL) &&
                  pluralize(classesCount, 'class', 'es')}
                {sessionType && sessionType === SESSION_TYPE_MAP.ALL && ' •'} 
                {sessionType &&
                  (sessionType === SESSION_TYPE_MAP.TESTS ||
                    sessionType === SESSION_TYPE_MAP.ALL) &&
                  pluralize(testsCount, 'mock test')}
              </WeekSubText>
            </div>
            {progressPercent > 0 &&
              progressPercent !== 100 &&
              !isWeekDisabled && (
                <InlineWrapper>
                  <Typography variant="p2" color="var(--color-text-secondary)">
                    {progressPercent}%
                  </Typography>
                  <LinearProgressBar
                    width="36px"
                    height="3px"
                    value={progressPercent}
                    backgroundColor="var(--color-progress-base-secondary)"
                    overlayColor="var(--color-base-fill)"
                    borderRadius="4px"
                  />
                </InlineWrapper>
              )}
            {progressPercent === 100 && !isWeekDisabled && (
              <Typography variant="p2" color="var(--color-text-secondary)">
                Completed
              </Typography>
            )}
          </WeekHeader>
        </StyledAccordionSummary>
        <StyledAccordionDetails>
          {filteredList?.map((lesson) => {
            return (
              <RecordedBatchItem
                key={lesson.properties.uid}
                lesson={lesson}
                forPlus
                isBatch
                onItemClick={onItemClick}
                showMoreMenu={false}
                showEducatorForLesson={false}
                showBookmark={hasAccessToBatch}
                goalUID={batchGoalUID}
                showLiveBatch
                isSubscribed={hasAccessToBatch}
                isRecordedBatchLesson
                $isExpanded={isExpanded}
                hasDppAccess={hasAccessToBatch}
              />
            );
          })}
        </StyledAccordionDetails>
      </StyledAccordian>
    </div>
  );
};

export default Week;
